import { useEffect } from 'react';

type IdleWindow = typeof window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
};

/**
 * Run `task` once during browser idle time after mount (with a setTimeout
 * fallback where requestIdleCallback is unavailable). Used to warm caches — e.g.
 * prefetch the catalog index — without competing with first paint. No-op unless
 * `enabled`.
 */
export function useIdleTask(task: () => void, enabled = true): void {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    const w = window as IdleWindow;
    let idleHandle: number | undefined;
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

    if (typeof w.requestIdleCallback === 'function') {
      idleHandle = w.requestIdleCallback(() => task(), { timeout: 2000 });
    } else {
      timeoutHandle = setTimeout(() => task(), 1200);
    }

    return () => {
      if (idleHandle !== undefined && typeof w.cancelIdleCallback === 'function') {
        w.cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
    };
  }, [task, enabled]);
}
